/**
 * `<caos-accordion>` — sections stacked one above another, each opened and closed from its own header.
 * (Catalog: Data display › Accordion — stacked sections, each opened and closed from its own header, so a long page is read a part at a time.)
 *
 * CAOS-593. A record page of eight sections is unusable when all eight are always open, and nothing
 * in the library collapsed more than itself. This is the piece that does.
 *
 * SECTIONS ARE A SHAPE, NOT A COMPONENT. The `sections` property is the list —
 * `{ value, label, open?, hidden? }[]` — and a section is nothing more than an entry in it, the same
 * fold as a menu item inside a menu. What a section HOLDS is placed inside the accordion with `slot`
 * set to that section's `value`, which is how a placed piece holds content (CAOS-654); a section with
 * nothing slotted opens onto nothing. A `value` that repeats an earlier one is dropped, because one
 * slot cannot be two sections.
 *
 * WHICH SECTIONS START OPEN is authored: the sections marked `open: true`. When no section says either
 * way, the first one opens, as the incumbent's does. By default one section is open at a time and
 * opening another closes it; `multiple` lets several stay open. A `hidden` section is left out
 * entirely — the conditional section — and its slotted content with it.
 *
 * REMEMBERED PER PERSON, where the author says so. Given a `remember` key, the sections a person opens
 * and closes are kept for them on this browser, under `caos.accordion.v1.<key>`, the same place the
 * workspace keeps every other per-person preference (see the host's `nav-preferences.ts`). Both halves
 * are kept — what they opened AND what they closed — so a section added to the page later starts as
 * authored instead of being mistaken for one they closed. Without the key every visit starts from the
 * authored state: what is worth remembering on a record page somebody returns to daily is not worth
 * remembering on a one-off form, and only the author knows which this is.
 *
 * SPACING. Nested inside a page section it adds none of its own: no outer margin or padding, a hairline
 * between sections, and each section's content indented by exactly the header's chevron and its gap,
 * so an accordion inside an accordion reads as nested without a number invented for it.
 *
 * A11y: each header is a real `<button>` carrying `aria-expanded` and `aria-controls`; each body is a
 * `role="region"` labelled by its header. Arrow Up/Down move between headers, Home/End go to the first
 * and last. A cancelable, bubbling, composed `caos-accordion-toggle` fires before a section opens or
 * closes — by pointer, keyboard, or `openSection` / `closeSection` / `toggleSection`. Styling seams:
 * `::part(accordion)`, `::part(section)`, `::part(header)`, `::part(label)`, `::part(panel)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

/** One section of an accordion — a shape inside the list, never a placed component of its own. */
export interface CaosAccordionSection {
  /** Names the section: in `open`, in the toggle event, and as the slot its content is placed into. */
  value: string;
  /** The words on the section's header. Falls back to `value`. */
  label: string;
  /** Starts open, before anybody has opened or closed anything. */
  open?: boolean;
  /** Left out of the accordion, and its content with it. */
  hidden?: boolean;
}

const STORAGE_PREFIX = 'caos.accordion.v1.';

let uid = 0;

const sheet = makeSheet(`
  :host { display: block; }

  .section + .section { border-top: 1px solid var(--caos-color-border); }

  .header {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    width: 100%;
    margin: 0;
    padding: var(--caos-space-3) 0;
    border: 0;
    background: transparent;
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    text-align: start;
    cursor: pointer;
  }
  .header:hover .chevron { color: var(--caos-color-text); }
  .header:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-radius: var(--caos-radius-sm); }

  /* The chevron points down when open, and toward the reading direction when closed. */
  .chevron {
    flex: none;
    width: 18px;
    height: 18px;
    color: var(--caos-color-text-muted);
    transform: rotate(-90deg);
    transition: transform 0.15s ease;
  }
  :host(:dir(rtl)) .chevron { transform: rotate(90deg); }
  .is-open .chevron,
  :host(:dir(rtl)) .is-open .chevron { transform: none; }
  .chevron svg { display: block; width: 100%; height: 100%; }

  .label { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }

  /* Indented by the chevron and its gap, so the content lines up under the label. */
  .panel {
    padding-block: 0 var(--caos-space-3);
    padding-inline: calc(18px + var(--caos-space-2)) 0;
  }
  .panel[hidden] { display: none; }

  @media (prefers-reduced-motion: reduce) {
    .chevron { transition: none; }
  }
`);

/** What a person opened and closed, kept both ways. */
interface Remembered {
  readonly open: readonly string[];
  readonly closed: readonly string[];
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** Never throws: storage may be unavailable, and the entry may be anything. */
function readRemembered(key: string): Remembered | undefined {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return undefined;
    const entry = parsed as Record<string, unknown>;
    return { open: strings(entry['open']), closed: strings(entry['closed']) };
  } catch {
    return undefined;
  }
}

function writeRemembered(key: string, value: Remembered): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage is unavailable: the choice lasts as long as the page does.
  }
}

/** Keep the entries that can be drawn: a string `value`, first use of it only. */
function normalise(list: unknown): CaosAccordionSection[] {
  if (!Array.isArray(list)) return [];
  const seen = new Set<string>();
  const out: CaosAccordionSection[] = [];
  for (const entry of list) {
    if (entry === null || typeof entry !== 'object') continue;
    const { value, label, open, hidden } = entry as Record<string, unknown>;
    if (typeof value !== 'string' || value === '' || seen.has(value)) continue;
    seen.add(value);
    out.push({
      value,
      label: typeof label === 'string' && label !== '' ? label : value,
      ...(typeof open === 'boolean' ? { open } : {}),
      ...(hidden === true ? { hidden: true } : {}),
    });
  }
  return out;
}

export class CaosAccordion extends CaosElement {
  static get observedAttributes(): string[] {
    return ['multiple', 'remember'];
  }

  private _sections: CaosAccordionSection[] = [];
  private openValues = new Set<string>();
  /** Somebody has opened or closed a section, so the starting state is no longer what shows. */
  private touched = false;
  /** The section the person opened most recently: the one kept when only one may stay open. */
  private lastOpened: string | undefined;
  private readonly idBase = `caos-accordion-${++uid}`;
  private readonly list: HTMLDivElement;
  private headers: HTMLButtonElement[] = [];

  constructor() {
    super(sheet);
    this.list = document.createElement('div');
    this.list.className = 'accordion';
    this.list.setAttribute('part', 'accordion');
    this.list.addEventListener('keydown', (e) => this.onKeydown(e));
    this.root.append(this.list);
  }

  /** The sections, in the order they are drawn (data in — a property, not an attribute). */
  get sections(): CaosAccordionSection[] {
    return this._sections;
  }
  set sections(list: CaosAccordionSection[]) {
    const known = new Set(this._sections.map((s) => s.value));
    this._sections = normalise(list);
    if (!this.touched) {
      this.openValues = this.startingState();
    } else {
      // Sections already on screen keep the state a person left them in; new ones start as authored,
      // by the same rule the first drawing used.
      const visible = this.visible();
      const authored = this.authoredOpen(visible);
      const kept = new Set([...this.openValues].filter((v) => visible.some((s) => s.value === v)));
      for (const s of visible) if (!known.has(s.value) && authored.includes(s.value)) kept.add(s.value);
      this.openValues = this.limit(kept, this.personsChoice());
    }
    this.render();
  }

  /** The values of the sections open now, in the order they are drawn. */
  get open(): string[] {
    return this.visible()
      .filter((s) => this.openValues.has(s.value))
      .map((s) => s.value);
  }

  /** Open a section. False when it is hidden, unknown, already open, or the event was cancelled. */
  openSection(value: string): boolean {
    return this.setOpen(value, true);
  }

  /** Close a section. False when it is hidden, unknown, already closed, or the event was cancelled. */
  closeSection(value: string): boolean {
    return this.setOpen(value, false);
  }

  /** Open a closed section or close an open one. */
  toggleSection(value: string): boolean {
    return this.setOpen(value, !this.openValues.has(value));
  }

  attributeChangedCallback(name: string): void {
    if (!this.touched) this.openValues = this.startingState();
    else if (name === 'multiple') this.openValues = this.limit(this.openValues, this.personsChoice());
    this.applyState();
  }

  private visible(): CaosAccordionSection[] {
    return this._sections.filter((s) => !s.hidden);
  }

  /** The sections the author opens: those marked open, or the first when no section says either way. */
  private authoredOpen(visible: readonly CaosAccordionSection[]): string[] {
    return visible.some((s) => s.open !== undefined)
      ? visible.filter((s) => s.open === true).map((s) => s.value)
      : visible.slice(0, 1).map((s) => s.value);
  }

  /** The open sections in the order the person would keep them: the one they opened last first. */
  private personsChoice(): string[] {
    const open = [...this.openValues];
    return this.lastOpened !== undefined && this.openValues.has(this.lastOpened)
      ? [this.lastOpened, ...open.filter((v) => v !== this.lastOpened)]
      : open;
  }

  /** What shows before anybody has touched it: what this person kept, else what the author set. */
  private startingState(): Set<string> {
    const visible = this.visible();
    const authored = this.authoredOpen(visible);
    const key = this.getAttribute('remember');
    const remembered = key ? readRemembered(key) : undefined;
    const open = new Set<string>();
    for (const s of visible) {
      if (remembered?.open.includes(s.value)) open.add(s.value);
      else if (remembered?.closed.includes(s.value)) continue;
      else if (authored.includes(s.value)) open.add(s.value);
    }
    return this.limit(open, remembered?.open ?? []);
  }

  /** One open section at most unless `multiple` — the one this person chose, else the first drawn. */
  private limit(open: Set<string>, prefer: readonly string[]): Set<string> {
    if (this.hasAttribute('multiple') || open.size <= 1) return open;
    const chosen = prefer.find((v) => open.has(v)) ?? this.visible().find((s) => open.has(s.value))?.value;
    return new Set(chosen === undefined ? [] : [chosen]);
  }

  private setOpen(value: string, open: boolean): boolean {
    if (!this.visible().some((s) => s.value === value)) return false;
    if (this.openValues.has(value) === open) return false;
    const event = new CustomEvent('caos-accordion-toggle', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { value, open },
    });
    if (!this.dispatchEvent(event)) return false;
    this.touched = true;
    const closedAlongside = open && !this.hasAttribute('multiple') ? [...this.openValues] : [];
    if (open) {
      if (!this.hasAttribute('multiple')) this.openValues.clear();
      this.openValues.add(value);
      this.lastOpened = value;
    } else {
      this.openValues.delete(value);
    }
    this.rememberChoice(value, open, closedAlongside);
    this.applyState();
    return true;
  }

  private rememberChoice(value: string, open: boolean, closedAlongside: readonly string[]): void {
    const key = this.getAttribute('remember');
    if (!key) return;
    const prior = readRemembered(key) ?? { open: [], closed: [] };
    const opened = new Set(prior.open);
    const closed = new Set(prior.closed);
    const mark = (v: string, isOpen: boolean): void => {
      (isOpen ? opened : closed).add(v);
      (isOpen ? closed : opened).delete(v);
    };
    mark(value, open);
    for (const v of closedAlongside) mark(v, false);
    writeRemembered(key, { open: [...opened], closed: [...closed] });
  }

  private render(): void {
    this.headers = [];
    const rows: HTMLElement[] = [];
    this.visible().forEach((section, i) => {
      const row = document.createElement('div');
      row.className = 'section';
      row.setAttribute('part', 'section');
      row.dataset['value'] = section.value;

      const header = document.createElement('button');
      header.type = 'button';
      header.className = 'header';
      header.setAttribute('part', 'header');
      header.id = `${this.idBase}-header-${i}`;
      header.setAttribute('aria-controls', `${this.idBase}-panel-${i}`);
      const chevron = document.createElement('span');
      chevron.className = 'chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.append(makeIcon('chevron'));
      const label = document.createElement('span');
      label.className = 'label';
      label.setAttribute('part', 'label');
      label.textContent = section.label;
      header.append(chevron, label);
      header.addEventListener('click', () => this.toggleSection(section.value));

      const panel = document.createElement('div');
      panel.className = 'panel';
      panel.setAttribute('part', 'panel');
      panel.id = `${this.idBase}-panel-${i}`;
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-labelledby', header.id);
      const slot = document.createElement('slot');
      slot.name = section.value;
      panel.append(slot);

      row.append(header, panel);
      rows.push(row);
      this.headers.push(header);
    });
    this.list.replaceChildren(...rows);
    this.applyState();
  }

  /**
   * Reflect the open set onto what is drawn, in place, so focus stays on the header pressed. The
   * lookup is this shadow root's own: a nested accordion's sections live in its shadow root, so they
   * are never reached from here.
   */
  private applyState(): void {
    for (const row of this.list.querySelectorAll<HTMLElement>('.section')) {
      const open = this.openValues.has(row.dataset['value'] ?? '');
      row.classList.toggle('is-open', open);
      row.querySelector('.header')?.setAttribute('aria-expanded', String(open));
      const panel = row.querySelector<HTMLElement>('.panel');
      if (panel) panel.hidden = !open;
    }
  }

  private onKeydown(e: KeyboardEvent): void {
    // Keys pressed inside a section's content arrive here too; only a header moves between headers.
    const at = this.headers.indexOf(e.target as HTMLButtonElement);
    if (at < 0) return;
    const last = this.headers.length - 1;
    const to =
      e.key === 'ArrowDown'
        ? at === last ? 0 : at + 1
        : e.key === 'ArrowUp'
          ? at === 0 ? last : at - 1
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : -1;
    if (to < 0) return;
    e.preventDefault();
    this.headers[to]?.focus();
  }
}

define('caos-accordion', CaosAccordion);

declare global {
  interface HTMLElementTagNameMap {
    'caos-accordion': CaosAccordion;
  }
}
